import React, { useState, useContext, useEffect } from 'react';
import { 
  Box, Typography, Card, CardContent, Divider, Chip, Tabs, Tab, 
  Grid, Alert, CircularProgress, Button
} from '@mui/material';
import { useQuery } from '@tanstack/react-query'; 

import AuthContext from './auth/Context'; 
import FeatureGate from './FeatureGate';

// Icons
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import InsightsIcon from '@mui/icons-material/Insights';
import DashboardIcon from '@mui/icons-material/Dashboard';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// Import DynamicDashboard
import DynamicDashboard from './DynamicDashboard';

const customScrollbarSx = {
  overflowY: 'auto',
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '4px' },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.2)' },
};

// Model `workflow` do gói mở rộng `flow` (flexbiz-server-flow) khai báo. Máy chủ chưa cài gói thì
// asyncGetList("workflow") trả rỗng/lỗi và màn hình chỉ hiện "không tìm thấy quy trình" — sai bản chất.
// Bọc FeatureGate ở dưới để nói đúng nguyên nhân: cần cài gói mở rộng.
function WorkflowViewerInner({ workflowData: initialWorkflowData, code, containerSx={} }) {
  const context = useContext(AuthContext);
  
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  // 👇 State quản lý việc mở rộng mô tả
  const [isDescExpanded, setIsDescExpanded] = useState(false);

  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  const { data: fetchedWorkflowData, isLoading, isError } = useQuery({
    queryKey: ['workflow_detail', code],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, "workflow", { code: code });
      const list = res?.data || res || [];
      return list.length > 0 ? list[0] : null;
    },
    enabled: !!code && !initialWorkflowData && !!userToken,
  });

  const workflowData = initialWorkflowData || fetchedWorkflowData;

  const handleTabChange = (event, newValue) => {
    setActiveStepIndex(newValue);
    setIsDescExpanded(false); // Reset lại trạng thái thu gọn khi chuyển tab
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
        <CircularProgress size={40} />
        <Typography color="text.secondary">Đang tải quy trình...</Typography>
      </Box>
    );
  }

  if (isError) return <Alert severity="error">Lỗi tải dữ liệu.</Alert>;
  if (!workflowData?.steps?.length) {
    return <Alert severity="warning">Quy trình trống.</Alert>;
  }

  const activeStep = workflowData.steps[activeStepIndex];
  const has_left_column = workflowData.kpi_configs?.length > 0 || activeStep?.time_block || activeStep?.objective || activeStep?.description;

  return (
    <Box sx={{ 
      width: '100%', 
      display: "flex", 
      flexDirection: "column", 
      height: { xs: 'auto', md: '100vh' }, 
      overflow: { xs: 'auto', md: 'hidden' }, 
      bgcolor: {md:'#f4f6f8'},
      ...containerSx 
    }}>
      
      {/* 1. HEADER (Tối ưu padding cho Mobile) */}
      <Box sx={{ p: { xs: 1, md: 2 }, bgcolor: '#ffffff', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: { xs: 1, md: 2 } }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" color="primary">{workflowData.title}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              <Chip label={workflowData.ma_bp || 'Chung'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              <Chip label={workflowData.status ? 'Đang chạy' : 'Dừng'} size="small" color={workflowData.status ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.7rem' }} />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">Mã: {workflowData.code}</Typography>
        </Box>
      </Box>

      {/* 2. THANH STEPS */}
      {workflowData.steps.length > 1 && (
        <Box sx={{ bgcolor: '#ffffff', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          <Tabs
            value={activeStepIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              minHeight: { xs: 44, md: 56 }, // Giảm chiều cao thanh tab trên mobile
              '& .MuiTab-root': { py: { xs: 0.5, md: 1 }, px: { xs: 1.5, md: 2 }, textTransform: 'none', minWidth: { xs: 90, md: 120 } },
              '& .Mui-selected': { bgcolor: '#f0f7ff' }
            }}
          >
            {workflowData.steps.map((step, index) => (
              <Tab key={index} label={
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="caption" color="text.secondary" display="block">Bước {index + 1}</Typography>
                  <Typography variant="body2" fontWeight={activeStepIndex === index ? 700 : 400}>{step.title}</Typography>
                </Box>
              } />
            ))}
          </Tabs>
        </Box>
      )}

      {/* 3. VÙNG NỘI DUNG CHÍNH */}
      <Box sx={{ 
        flexGrow: 1, 
        p: { xs: 0, md: 2 }, // Giảm padding tổng bao quanh
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0, 
        overflow: { xs: 'visible', md: 'hidden' }
      }}>
        {/* 👇 Thu hẹp khoảng cách spacing giữa 2 cột trên Mobile (xs=1, md=2) */}
        <Grid container spacing={{ xs: 0, md: 2 }} sx={{ flexGrow: 1, height: { xs: 'auto', md: '100%' } }}>
          
          {/* CỘT 1 (md=3): MÔ TẢ & KPI */}
          {!!has_left_column && <Grid size={{ xs: 12, md: 3 }} sx={{ height: { xs: 'auto', md: '100%' } }}>
            <Box sx={{ height: '100%', ...customScrollbarSx, pr: { xs: 0, md: 1 }, pb: { xs: 0, md: 1 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, md: 2 } }}>
                
                <Card variant="outlined" sx={{ borderRadius: {md: 2} }}>
                  <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">Nội dung công việc</Typography>
                      {activeStep?.time_block && (
                        <Chip icon={<AccessTimeIcon fontSize="small" />} label={activeStep.time_block} size="small" color="primary" variant="outlined" sx={{ bgcolor: '#f0f7ff', height: 20, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                    
                    {activeStep?.objective && (
                      <Alert severity="info" sx={{ mb: 1.5, p: 0.5, py: 0, '& .MuiAlert-icon': { fontSize: 18, mr: 1 } }}>
                        <Typography variant="body2">{activeStep.objective}</Typography>
                      </Alert>
                    )}

                    {/* 👇 TÍNH NĂNG MỚI: ẨN HIỆN DESCRIPTION VỚI HIỆU ỨNG MỜ */}
                    {activeStep?.description && (
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ position: 'relative' }}>
                          <Box 
                            className="rich-text-content"
                            sx={{ 
                              fontSize: '0.85rem', color: '#444',
                              '& p': { mb: 1, lineHeight: 1.5 },
                              '& ul, & ol': { pl: 2, mb: 1 }, '& li': { mb: 0.25 },
                              // CSS Thu gọn: Khóa ở 3 dòng
                              ...(!isDescExpanded && {
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              })
                            }}
                            dangerouslySetInnerHTML={{ __html: activeStep.description }} 
                          />
                          
                          {/* Lớp phủ mờ (Fade Out) khi bị thu gọn */}
                          {!isDescExpanded && (
                            <Box 
                              sx={{
                                position: 'absolute', bottom: 0, left: 0, right: 0, height: '30px',
                                background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))',
                                pointerEvents: 'none'
                              }} 
                            />
                          )}
                        </Box>

                        <Button 
                          size="small" 
                          onClick={() => setIsDescExpanded(!isDescExpanded)}
                          endIcon={isDescExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          sx={{ mt: 0.5, p: 0, minHeight: 0, textTransform: 'none', fontSize: '0.8rem' }}
                        >
                          {isDescExpanded ? 'Thu gọn' : 'Xem thêm'}
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {workflowData.kpi_configs?.length > 0 && (
                  <Card variant="outlined" sx={{ borderRadius: {md:2} }}>
                    <Box sx={{ p: { xs: 1, md: 1.5 }, bgcolor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
                      <Typography variant="caption" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InsightsIcon fontSize="inherit" color="warning" /> CHỈ SỐ KPI
                      </Typography>
                    </Box>
                    <Box sx={{ p: { xs: 1, md: 1.5 } }}>
                      {workflowData.kpi_configs.map((kpi, idx) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">{kpi.metric_name}</Typography>
                          <Typography variant="caption" fontWeight="bold">{kpi.target_value || 'Auto'}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Card>
                )}
              </Box>
            </Box>
          </Grid>}

          {/* CỘT 2 (md=9): DASHBOARD AREA */}
          <Grid size={{ xs: 12, md: has_left_column?9:12 }} sx={{ height: { xs: 'auto', md: '100%' } }}>
            <Box sx={{ 
                height: { xs: 'auto', md: '100%' }, 
                display: 'flex', 
                flexDirection: 'column',
                bgcolor: '#ffffff', 
                borderRadius: {md:2}, 
                border: '1px solid #e0e0e0',
                overflow: 'hidden',
                pb: { xs: 1, md: 0 }
              }}>
              <Box sx={{ p: { xs: 1, md: 1.5 }, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', bgcolor: '#fafafa' }}>
                <DashboardIcon sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold">Không gian làm việc</Typography>
              </Box>

              <Box sx={{ flexGrow: 1, position: { xs: 'static', md: 'relative' } }}>
                {activeStep?.dashboard_config ? (
                  <DynamicDashboard 
                    dfWidgets={activeStep.dashboard_config} 
                    hideHeader={true} 
                    readOnly={true} 
                    containerSx={{ 
                      position: { xs: 'static', md: 'absolute' }, 
                      top: 0, left: 0, right: 0, bottom: 0,
                      height: { xs: 'auto', md: '100%' },
                      overflow: { xs: 'visible', md: 'hidden' },
                      borderRadius: 0 
                    }} 
                    contentSx={{
                      overflowY: { xs: 'visible', md: 'auto' },
                      height: { xs: 'auto', md: '100%' },
                      // Giảm padding nội bộ của Dashboard trên mobile
                      px: { xs: 0, md: 1 },
                      pt: { xs: 1, md: 1 }
                    }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, color: 'text.secondary' }}>
                    <Typography variant="body2">Chưa thiết lập thao tác.</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>

        </Grid>
      </Box>
    </Box>
  );
}

export default function WorkflowViewer(props) {
  return (
    <FeatureGate feature="flow">
      <WorkflowViewerInner {...props} />
    </FeatureGate>
  );
}
